import type { ReactNode } from 'react';

import { Button } from '@packages/ui/shared';
import { Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5SectionTitle } from './ssm5-section-title';

/** A footer action of the modal. */
export type Ssm5ModalAction = {
    /** Called when the action is pressed. */
    onPress: () => void;
    /** The action label. */
    title: string;
};

/** The two footer actions of a decision modal. */
export type Ssm5ModalActions = {
    /** The action that goes ahead, rendered as the primary button. */
    primary: Ssm5ModalAction;
    /** The action that holds back, rendered as the ghost button before the primary one. */
    secondary: Ssm5ModalAction;
};

/** Props for {@link Ssm5Modal}. */
export type Ssm5ModalProps = {
    /** The footer actions; without them the modal has no footer. */
    actions?: Ssm5ModalActions;
    /** The body content. */
    children: ReactNode;
    /** Label of the header close button; without it the header has no close button. */
    closeLabel?: string;
    /** The dialog look from the design; flat without it. */
    look?: Ssm5ModalLook;
    /** Called when the modal wants to close. */
    onHide: () => void;
    /** Whether the modal is visible. */
    show: boolean;
    /** The header title. */
    title: string;
    /** The dialog width. */
    width: number;
};

/** The dialog looks the designs draw: corner radius and shadow of each. */
const DIALOG_LOOKS = {
    /** A card, with the design's two Card Shadows. */
    card: { boxShadow: '-4px -4px 12px rgba(0, 0, 0, 0.04), 4px 4px 12px rgba(0, 0, 0, 0.05)', radius: 4 },
    /** A flat dialog, without a shadow. */
    flat: { boxShadow: 'none', radius: 8 },
} as const;

/** A dialog look from the design. */
export type Ssm5ModalLook = keyof typeof DIALOG_LOOKS;

/** The footer buttons in render order, with the variant each action takes. */
const FOOTER_BUTTONS = [
    { action: 'secondary', variant: 'ghost-blue' },
    { action: 'primary', variant: 'primary' },
] as const;

const useStyles = createUseStyles({
    dialog: {
        maxWidth: 'none',
        width: ({ width }: Pick<Ssm5ModalProps, 'width'>) => width,
        '& .modal-content': {
            borderRadius: ({ look }: Pick<Required<Ssm5ModalProps>, 'look'>) => DIALOG_LOOKS[look].radius,
            boxShadow: ({ look }: Pick<Required<Ssm5ModalProps>, 'look'>) => DIALOG_LOOKS[look].boxShadow,
            overflow: 'hidden',
        },
    },
    header: {
        '&.subcomm-modal__header': {
            alignItems: 'center',
            display: 'flex',
            minHeight: 64,
            padding: '16px 24px',
        },
    },
    body: {
        '&.subcomm-modal__body': {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: 24,
        },
    },
    footer: {
        '&.subcomm-modal__footer': {
            alignItems: 'center',
            gap: 8,
            // The design's 1px top stroke takes no height, so the top border comes out of the 24px padding.
            padding: '23px 24px 24px',
        },
    },
});

/**
 * The SSM5 modal frame: grey 64px header with the title, 24px body, and the two decision buttons in
 * the footer when actions are given.
 *
 * @param props - {@link Ssm5ModalProps}
 * @returns the modal
 */
export function Ssm5Modal({
    actions,
    children,
    closeLabel,
    look = 'flat',
    onHide,
    show,
    title,
    width,
}: Ssm5ModalProps) {
    const classes = useStyles({ look, width });

    return (
        <Modal centered dialogClassName={classes.dialog} onHide={onHide} show={show}>
            <Modal.Header className={classes.header} closeButton={closeLabel !== undefined} closeLabel={closeLabel}>
                <Ssm5SectionTitle>{title}</Ssm5SectionTitle>
            </Modal.Header>
            <Modal.Body className={classes.body}>{children}</Modal.Body>
            {actions && (
                <Modal.Footer className={classes.footer}>
                    {FOOTER_BUTTONS.map(({ action, variant }) => {
                        const { onPress, title: actionTitle } = actions[action];

                        return (
                            <Button
                                key={action}
                                onPress={onPress}
                                style={ssm5ControlStyles.primaryButton}
                                title={actionTitle}
                                titleStyle={ssm5TextStyles.primaryButtonTitle}
                                variant={variant}
                            />
                        );
                    })}
                </Modal.Footer>
            )}
        </Modal>
    );
}
